import { useState, useEffect, useRef, useMemo } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''

/**
 * CraftMenu — searchable alphabetical craft type selector
 * 81 craft types across 19 categories, searchable and filterable.
 */
export default function CraftMenu({ isOpen, onClose, onSelect, currentCraft }) {
  const [crafts, setCrafts] = useState([])
  const [categories, setCategories] = useState([])
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState(null)
  const [loading, setLoading] = useState(true)
  const searchRef = useRef(null)
  const menuRef = useRef(null)

  // Fetch craft types on mount
  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    fetch(`${API_URL}/export/craft-types`)
      .then(r => r.json())
      .then(data => {
        setCrafts(data.crafts || [])
        setCategories(data.categories || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [isOpen])

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Close on escape
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen, onClose])

  // Filter crafts
  const filteredCrafts = useMemo(() => {
    let result = crafts
    if (search) {
      const s = search.toLowerCase()
      result = result.filter(c =>
        c.name.toLowerCase().includes(s) ||
        c.key.toLowerCase().includes(s) ||
        c.category.toLowerCase().includes(s)
      )
    }
    if (activeCategory) {
      result = result.filter(c => c.category === activeCategory)
    }
    return result
  }, [crafts, search, activeCategory])

  // Group by category
  const grouped = useMemo(() => {
    const groups = {}
    filteredCrafts.forEach(c => {
      if (!groups[c.category]) groups[c.category] = []
      groups[c.category].push(c)
    })
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]))
  }, [filteredCrafts])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div ref={menuRef} className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-[600px] max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-white">Choose a Craft</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none">&times;</button>
          </div>

          {/* Search */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search 81 craft types..."
              value={search}
              onChange={e => { setSearch(e.target.value); setActiveCategory(null) }}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-sm">
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Category tabs */}
        <div className="px-3 py-2 border-b border-slate-700/50 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
              !activeCategory ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
          >
            All ({crafts.length})
          </button>
          {categories.map(cat => {
            const count = crafts.filter(c => c.category === cat).length
            return (
              <button
                key={cat}
                onClick={() => { setActiveCategory(activeCategory === cat ? null : cat); setSearch('') }}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  activeCategory === cat ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                {cat} ({count})
              </button>
            )
          })}
        </div>

        {/* Craft list */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">Loading crafts...</div>
          ) : filteredCrafts.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-slate-500">No crafts match "{search}"</div>
          ) : (
            grouped.map(([category, items]) => (
              <div key={category} className="mb-3">
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">{category}</div>
                <div className="grid grid-cols-2 gap-1">
                  {items.map(craft => (
                    <button
                      key={craft.key}
                      onClick={() => { onSelect(craft); onClose() }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                        currentCraft === craft.key
                          ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <span className="truncate">{craft.name}</span>
                      {currentCraft === craft.key && (
                        <svg className="w-4 h-4 text-blue-400 flex-shrink-0 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-700 flex items-center justify-between text-xs text-slate-500">
          <span>{filteredCrafts.length} of {crafts.length} crafts</span>
          {currentCraft && (
            <span className="text-blue-400">Current: {crafts.find(c => c.key === currentCraft)?.name || currentCraft}</span>
          )}
        </div>
      </div>
    </div>
  )
}
